import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { PlusCircle, Search, Loader2, X, Sparkles, Trash2, Save, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import IntelligenceHub from "./IntelligenceHub";
import { ComplianceBadge, ScorePill, SectionTitle } from "./atoms";
import { STAGE_LABELS } from "./stages";

const inputCls =
  "w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2 text-sm text-[#1F2937]";
const labelCls = "block text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1.5";

export default function TargetList() {
  const [params, setParams] = useSearchParams();
  const [vocab, setVocab] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "", target_type: "", stage: "", priority: "", persona: "", city: "", country: "",
  });
  const [showForm, setShowForm] = useState(params.get("new") === "1");
  const [editing, setEditing] = useState(null);       // full target when editing
  const [detail, setDetail] = useState(null);         // opened detail drawer

  // Load vocabulary once
  useEffect(() => {
    api.get("/cdl/vocabulary").then((r) => setVocab(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
      const { data } = await api.get(`/cdl/targets?${qs.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Impossible de charger les cibles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [
    filters.q, filters.target_type, filters.stage, filters.priority,
    filters.persona, filters.city, filters.country,
  ]);

  // Open target detail from radar deep link
  useEffect(() => {
    const tid = params.get("target");
    if (tid) openDetail(tid);
  }, [params]);

  const openDetail = async (tid) => {
    try {
      const { data } = await api.get(`/cdl/targets/${tid}`);
      setDetail(data);
    } catch (e) {
      toast.error("Cible introuvable.");
    }
  };

  const closeAll = () => {
    setShowForm(false); setEditing(null); setDetail(null);
    if (params.get("target") || params.get("new")) {
      const p = new URLSearchParams(params);
      p.delete("target"); p.delete("new");
      setParams(p, { replace: true });
    }
  };

  return (
    <IntelligenceHub activeTab="list">
      <div className="space-y-6">
        <FilterBar
          vocab={vocab}
          filters={filters}
          setFilters={setFilters}
          onNew={() => { setEditing(null); setShowForm(true); }}
        />

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#5B5B62]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" strokeWidth={1.8} /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#E4E1D8] bg-white/40 px-6 py-16 text-center">
            <div className="text-sm font-medium text-[#0B2545]">Aucune cible ne correspond aux filtres</div>
            <button
              data-testid="intel-add-first-target"
              onClick={() => setShowForm(true)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-4 py-2 text-sm transition-colors"
            >
              <PlusCircle className="h-4 w-4" strokeWidth={1.8} /> Ajouter une cible
            </button>
          </div>
        ) : (
          <div className="rounded-md border border-[#E4E1D8] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F7F3] text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                <tr>
                  <th className="text-left px-4 py-2.5">Cible</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-left px-4 py-2.5">Ville</th>
                  <th className="text-left px-4 py-2.5">Étape</th>
                  <th className="text-left px-4 py-2.5">Conformité</th>
                  <th className="text-right px-4 py-2.5">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F2EFE7]">
                {items.map((t) => (
                  <tr
                    key={t.target_id}
                    data-testid={`intel-row-${t.target_id}`}
                    onClick={() => openDetail(t.target_id)}
                    className="cursor-pointer hover:bg-[#F8F7F3]"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1F2937]">{t.display_name}</div>
                      <div className="text-[11px] text-[#5B5B62]">
                        {[t.organization_name, t.role_or_title].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#5B5B62]">{t.target_type}</td>
                    <td className="px-4 py-3 text-xs text-[#5B5B62]">{[t.city, t.country].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#0B2545]">
                        {STAGE_LABELS[t.stage] || t.stage}
                      </span>
                    </td>
                    <td className="px-4 py-3"><ComplianceBadge status={t.compliance_status} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-lg font-semibold text-[#0B2545]">{t.ai_score ?? "—"}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showForm || editing) && vocab && (
        <TargetForm
          vocab={vocab}
          initial={editing}
          onClose={closeAll}
          onSaved={(t) => { closeAll(); load(); openDetail(t.target_id); }}
        />
      )}
      {detail && (
        <TargetDetail
          data={detail}
          onClose={closeAll}
          onEdit={() => { setEditing(detail.target); setDetail(null); }}
          onChanged={async (tid) => { await load(); await openDetail(tid); }}
        />
      )}
    </IntelligenceHub>
  );
}

function FilterBar({ vocab, filters, setFilters, onNew }) {
  const set = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B8B8F]" strokeWidth={1.8} />
        <input
          data-testid="intel-filter-q"
          placeholder="Rechercher (nom, organisation, email, notes…)"
          value={filters.q}
          onChange={(e) => set("q", e.target.value)}
          className={`${inputCls} pl-9`}
        />
      </div>
      <Select testId="intel-filter-type" value={filters.target_type} onChange={(v) => set("target_type", v)} placeholder="Tous les types">
        {(vocab?.target_types || []).map((o) => <option key={o.key} value={o.key}>{o.label_fr}</option>)}
      </Select>
      <Select testId="intel-filter-stage" value={filters.stage} onChange={(v) => set("stage", v)} placeholder="Toutes étapes">
        {(vocab?.stages || []).map((o) => <option key={o.key} value={o.key}>{o.label_fr}</option>)}
      </Select>
      <Select testId="intel-filter-persona" value={filters.persona} onChange={(v) => set("persona", v)} placeholder="Tous personas">
        {(vocab?.personas || []).map((o) => <option key={o.key} value={o.key}>{o.label_fr}</option>)}
      </Select>
      <input placeholder="Ville" value={filters.city} onChange={(e)=>set("city", e.target.value)} className={`${inputCls} max-w-[140px]`} data-testid="intel-filter-city" />
      <button
        data-testid="intel-new-target"
        onClick={onNew}
        className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-4 py-2 text-sm transition-colors"
      >
        <PlusCircle className="h-4 w-4" strokeWidth={1.8} /> Nouvelle cible
      </button>
    </div>
  );
}

function Select({ value, onChange, placeholder, children, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} max-w-[200px]`}
    >
      <option value="">{placeholder}</option>
      {children}
    </select>
  );
}

// ============================================================================
// Target form (create/edit)
// ============================================================================
function TargetForm({ vocab, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(() => ({
    target_type: initial?.target_type || "prospect",
    display_name: initial?.display_name || "",
    first_name: initial?.first_name || "",
    last_name: initial?.last_name || "",
    organization_name: initial?.organization_name || "",
    role_or_title: initial?.role_or_title || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    linkedin_url: initial?.linkedin_url || "",
    website: initial?.website || "",
    country: initial?.country || "",
    province: initial?.province || "",
    city: initial?.city || "",
    language: initial?.language || "fr",
    community: initial?.community || "",
    sector: initial?.sector || "",
    profession: initial?.profession || "",
    personas: initial?.personas || [],
    priority: initial?.priority || "medium",
    immigrant_market_link: !!initial?.immigrant_market_link,
    source_du_contact: initial?.source_du_contact || "",
    source_detail: initial?.source_detail || "",
    share_scope: initial?.share_scope || "private",
    consent_channels: initial?.consent_channels || [],
    forbidden_channels: initial?.forbidden_channels || [],
    notes: initial?.notes || "",
  }));

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const togglePersona = (p) => set("personas", f.personas.includes(p) ? f.personas.filter(x=>x!==p) : [...f.personas, p]);
  const toggleChan = (list, c) => {
    const cur = f[list];
    set(list, cur.includes(c) ? cur.filter(x=>x!==c) : [...cur, c]);
  };

  const save = async () => {
    if (!f.display_name.trim() || !f.source_du_contact.trim()) {
      toast.error("Nom et source du contact obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const { data } = isEdit
        ? await api.patch(`/cdl/targets/${initial.target_id}`, payload)
        : await api.post("/cdl/targets", payload);
      toast.success(isEdit ? "Cible mise à jour." : "Cible créée.");
      onSaved(isEdit ? data.target : data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer onClose={onClose} title={isEdit ? "Modifier la cible" : "Nouvelle cible"} width="max-w-2xl">
      <div className="space-y-5">
        <SectionTitle sub="Identifiez précisément la cible et son contexte.">Identité</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nom affiché *">
            <input data-testid="tform-name" value={f.display_name} onChange={(e)=>set("display_name", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Type de cible *">
            <select data-testid="tform-type" value={f.target_type} onChange={(e)=>set("target_type", e.target.value)} className={inputCls}>
              {(vocab.target_types || []).map((o) => <option key={o.key} value={o.key}>{o.label_fr}</option>)}
            </select>
          </Field>
          <Field label="Prénom"><input value={f.first_name} onChange={(e)=>set("first_name", e.target.value)} className={inputCls} /></Field>
          <Field label="Nom de famille"><input value={f.last_name} onChange={(e)=>set("last_name", e.target.value)} className={inputCls} /></Field>
          <Field label="Organisation"><input value={f.organization_name} onChange={(e)=>set("organization_name", e.target.value)} className={inputCls} /></Field>
          <Field label="Rôle / titre"><input value={f.role_or_title} onChange={(e)=>set("role_or_title", e.target.value)} className={inputCls} /></Field>
        </div>

        <SectionTitle sub="Comment joindre cette cible (optionnel).">Coordonnées</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Courriel"><input type="email" value={f.email} onChange={(e)=>set("email", e.target.value)} className={inputCls} /></Field>
          <Field label="Téléphone"><input value={f.phone} onChange={(e)=>set("phone", e.target.value)} className={inputCls} placeholder="+1 514 000 0000" /></Field>
          <Field label="LinkedIn"><input value={f.linkedin_url} onChange={(e)=>set("linkedin_url", e.target.value)} className={inputCls} /></Field>
          <Field label="Site Web"><input value={f.website} onChange={(e)=>set("website", e.target.value)} className={inputCls} /></Field>
        </div>

        <SectionTitle sub="Localisation et alignement avec votre stratégie.">Segmentation</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Pays (ISO-2)"><input value={f.country} onChange={(e)=>set("country", e.target.value.toUpperCase())} className={inputCls} placeholder="CA / CH" /></Field>
          <Field label="Province"><input value={f.province} onChange={(e)=>set("province", e.target.value)} className={inputCls} /></Field>
          <Field label="Ville"><input value={f.city} onChange={(e)=>set("city", e.target.value)} className={inputCls} /></Field>
          <Field label="Langue"><input value={f.language} onChange={(e)=>set("language", e.target.value)} className={inputCls} placeholder="fr" /></Field>
          <Field label="Communauté"><input value={f.community} onChange={(e)=>set("community", e.target.value)} className={inputCls} /></Field>
          <Field label="Secteur"><input value={f.sector} onChange={(e)=>set("sector", e.target.value)} className={inputCls} /></Field>
          <Field label="Profession"><input value={f.profession} onChange={(e)=>set("profession", e.target.value)} className={inputCls} /></Field>
          <Field label="Priorité">
            <select data-testid="tform-priority" value={f.priority} onChange={(e)=>set("priority", e.target.value)} className={inputCls}>
              {(vocab.priorities || []).map((o) => <option key={o.key} value={o.key}>{o.label_fr}</option>)}
            </select>
          </Field>
          <Field label="Partage">
            <select data-testid="tform-scope" value={f.share_scope} onChange={(e)=>set("share_scope", e.target.value)} className={inputCls}>
              {(vocab.share_scopes || []).map((o) => <option key={o.key} value={o.key}>{o.label_fr}</option>)}
            </select>
          </Field>
        </div>

        <div>
          <div className={labelCls}>Personas</div>
          <div className="flex flex-wrap gap-1.5">
            {(vocab.personas || []).map((p) => {
              const active = f.personas.includes(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => togglePersona(p.key)}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${active ? "bg-[#0B2545] text-white border-[#0B2545]" : "bg-white border-[#E4E1D8] text-[#5B5B62] hover:border-[#0B2545]"}`}
                >
                  {p.label_fr}
                </button>
              );
            })}
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-[#3A3A3F]">
          <input type="checkbox" checked={f.immigrant_market_link} onChange={(e)=>set("immigrant_market_link", e.target.checked)} className="rounded border-[#DCC29A]" />
          Lien avec le marché immigrant
        </label>

        <SectionTitle sub="La provenance du contact est obligatoire (traçabilité + conformité).">Provenance</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Source du contact *">
            <input
              data-testid="tform-source"
              value={f.source_du_contact}
              onChange={(e)=>set("source_du_contact", e.target.value)}
              placeholder="ex : événement CCM 12 mars 2026 · référence de client · annuaire OACIQ"
              className={inputCls}
            />
          </Field>
          <Field label="Détail de la source">
            <input value={f.source_detail} onChange={(e)=>set("source_detail", e.target.value)} className={inputCls} />
          </Field>
        </div>

        <SectionTitle sub="Canaux autorisés / interdits (base de la conformité).">Consentement</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className={labelCls}>Canaux consentis</div>
            <div className="flex flex-wrap gap-1.5">
              {(vocab.channels || []).map((c) => {
                const active = f.consent_channels.includes(c.key);
                return (
                  <button key={c.key} type="button" onClick={() => toggleChan("consent_channels", c.key)}
                    className={`rounded-full px-3 py-1 text-xs border transition-colors ${active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-[#E4E1D8] text-[#5B5B62] hover:border-emerald-600"}`}>
                    {c.label_fr}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className={labelCls}>Canaux interdits</div>
            <div className="flex flex-wrap gap-1.5">
              {(vocab.channels || []).map((c) => {
                const active = f.forbidden_channels.includes(c.key);
                return (
                  <button key={c.key} type="button" onClick={() => toggleChan("forbidden_channels", c.key)}
                    className={`rounded-full px-3 py-1 text-xs border transition-colors ${active ? "bg-rose-600 text-white border-rose-600" : "bg-white border-[#E4E1D8] text-[#5B5B62] hover:border-rose-600"}`}>
                    {c.label_fr}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <Field label="Notes libres">
          <textarea rows={3} value={f.notes} onChange={(e)=>set("notes", e.target.value)} className={inputCls} />
        </Field>

        <div className="flex justify-end gap-2 pt-3 border-t border-[#F2EFE7]">
          <button onClick={onClose} className="text-sm text-[#5B5B62] hover:text-[#0B2545] px-4 py-2">Annuler</button>
          <button
            data-testid="tform-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-5 py-2 text-sm transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <Save className="h-4 w-4" strokeWidth={1.8} />}
            {isEdit ? "Enregistrer" : "Créer la cible"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}

// ============================================================================
// Target detail drawer
// ============================================================================
function TargetDetail({ data, onClose, onEdit, onChanged }) {
  const t = data.target;
  const [qualifying, setQualifying] = useState(false);
  const [checking, setChecking] = useState(false);

  const runQualify = async () => {
    setQualifying(true);
    try {
      const { data: d } = await api.post(`/cdl/targets/${t.target_id}/qualify`);
      toast.success(`Qualification IA — score ${d.target.ai_score}/100`);
      onChanged(t.target_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason_fr || e?.response?.data?.detail || "Qualification impossible.");
    } finally {
      setQualifying(false);
    }
  };

  const runCompliance = async () => {
    setChecking(true);
    try {
      const { data: d } = await api.post(`/cdl/targets/${t.target_id}/compliance-check`, { channels: null });
      toast.success(`Conformité : ${d.status}`);
      onChanged(t.target_id);
    } catch (e) {
      toast.error("Vérification échouée.");
    } finally {
      setChecking(false);
    }
  };

  const changeStage = async (stage) => {
    try {
      await api.post(`/cdl/targets/${t.target_id}/stage`, { stage });
      toast.success(`Étape : ${STAGE_LABELS[stage] || stage}`);
      onChanged(t.target_id);
    } catch (e) {
      toast.error("Changement d'étape impossible.");
    }
  };

  const remove = async () => {
    if (!confirm("Supprimer définitivement cette cible et son historique ?")) return;
    try {
      await api.delete(`/cdl/targets/${t.target_id}`);
      toast.success("Cible supprimée.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  return (
    <Drawer onClose={onClose} title={t.display_name} width="max-w-3xl">
      <div className="space-y-6">
        <div className="flex items-start gap-3 flex-wrap">
          <ComplianceBadge status={t.compliance_status} />
          <span className="rounded-full px-3 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#F2EFE7] text-[#0B2545] border border-[#E4E1D8]">
            {STAGE_LABELS[t.stage] || t.stage}
          </span>
          <span className="rounded-full px-3 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-white border border-[#E4E1D8] text-[#5B5B62]">
            {t.target_type}
          </span>
          <div className="ml-auto flex gap-2">
            <button
              data-testid="tdetail-qualify"
              onClick={runQualify} disabled={qualifying}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-3 py-1.5 text-xs transition-colors disabled:opacity-60"
            >
              {qualifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.8} /> : <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />}
              Qualifier avec l&apos;IA
            </button>
            <button
              data-testid="tdetail-compliance"
              onClick={runCompliance} disabled={checking}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white font-medium px-3 py-1.5 text-xs transition-colors"
            >
              {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.8} /> : <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.8} />}
              Vérifier la conformité
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
          <ScorePill label="Pertinence" value={t.ai_score} />
          <ScorePill label="Impact" value={t.ai_impact} tone="gold" />
          <ScorePill label="Probabilité" value={t.ai_probability_success} tone="ghost" />
          <ScorePill label="Confiance" value={t.ai_confidence} tone="ghost" />
          <ScorePill label="Potentiel client" value={t.ai_client_potential} />
          <ScorePill label="Potentiel partenaire" value={t.ai_partner_potential} tone="gold" />
          <ScorePill label="Potentiel référence" value={t.ai_referral_potential} tone="ghost" />
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#8B8B8F]">Urgence</div>
            <div className="mt-1 text-sm font-medium text-[#0B2545] uppercase">{t.ai_urgency || "—"}</div>
          </div>
        </div>

        {(t.ai_angle_fr || t.ai_reasons_fr) && (
          <div className="rounded-md border border-[#E4E1D8] bg-[#FDFCF7] p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-[#DCC29A]" strokeWidth={1.8} />
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                Copilote — {t.ai_model || "IA"}
              </span>
            </div>
            {t.ai_angle_fr && <p className="text-sm text-[#1F2937] font-medium">{t.ai_angle_fr}</p>}
            {t.ai_reasons_fr && <p className="mt-2 text-xs text-[#3A3A3F] whitespace-pre-line">{t.ai_reasons_fr}</p>}
            {t.ai_first_message_fr && (
              <div className="mt-3 rounded-md bg-white border border-[#E4E1D8] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">Message suggéré</div>
                <p className="text-xs text-[#1F2937] whitespace-pre-line">{t.ai_first_message_fr}</p>
              </div>
            )}
            {t.ai_next_action_fr && (
              <div className="mt-3 text-xs text-[#0B2545]">
                <span className="font-mono uppercase tracking-widest text-[10px] mr-2">Prochaine action</span>
                {t.ai_next_action_fr}
              </div>
            )}
          </div>
        )}

        {t.compliance_reason && (
          <div className="rounded-md border border-[#E4E1D8] bg-white p-4 text-xs text-[#3A3A3F]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">Vérification conformité</div>
            {t.compliance_reason}
          </div>
        )}

        <div>
          <div className={labelCls}>Déplacer dans le pipeline</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(STAGE_LABELS).map(([k, label]) => {
              const active = t.stage === k;
              return (
                <button key={k} onClick={() => changeStage(k)}
                  data-testid={`tdetail-stage-${k}`}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${active ? "bg-[#0B2545] text-white border-[#0B2545]" : "bg-white border-[#E4E1D8] text-[#5B5B62] hover:border-[#0B2545]"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <InfoRow k="Courriel" v={t.email} />
          <InfoRow k="Téléphone" v={t.phone} />
          <InfoRow k="LinkedIn" v={t.linkedin_url} />
          <InfoRow k="Site web" v={t.website} />
          <InfoRow k="Ville" v={[t.city, t.province, t.country].filter(Boolean).join(", ")} />
          <InfoRow k="Langue" v={t.language} />
          <InfoRow k="Secteur" v={t.sector} />
          <InfoRow k="Profession" v={t.profession} />
          <InfoRow k="Source" v={t.source_du_contact} />
          <InfoRow k="Détail source" v={t.source_detail} />
          <InfoRow k="Canaux autorisés" v={(t.consent_channels || []).join(", ")} />
          <InfoRow k="Canaux interdits" v={(t.forbidden_channels || []).join(", ")} />
        </div>

        {t.notes && (
          <div>
            <div className={labelCls}>Notes</div>
            <div className="rounded-md border border-[#E4E1D8] bg-white p-3 text-xs whitespace-pre-line text-[#3A3A3F]">{t.notes}</div>
          </div>
        )}

        {data.activities?.length > 0 && (
          <div>
            <SectionTitle sub="Historique auditif complet des actions et vérifications.">Journal d&apos;activités</SectionTitle>
            <div className="rounded-md border border-[#E4E1D8] bg-white divide-y divide-[#F2EFE7] max-h-64 overflow-auto">
              {data.activities.map((a) => (
                <div key={a.activity_id} className="px-4 py-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                    {new Date(a.created_at).toLocaleString("fr-CA")} · {a.kind}
                  </div>
                  <div className="text-xs text-[#1F2937] mt-0.5">{a.summary}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between pt-4 border-t border-[#F2EFE7]">
          <button data-testid="tdetail-delete" onClick={remove} className="inline-flex items-center gap-1.5 text-xs text-rose-700 hover:text-rose-900">
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} /> Supprimer
          </button>
          <button data-testid="tdetail-edit" onClick={onEdit} className="rounded-md border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white font-medium px-4 py-1.5 text-xs transition-colors">
            Modifier
          </button>
        </div>
      </div>
    </Drawer>
  );
}

function InfoRow({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#8B8B8F]">{k}</div>
      <div className="text-[#1F2937]">{v || <span className="text-[#8B8B8F]">—</span>}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className={labelCls}>{label}</div>{children}</label>;
}

function Drawer({ title, width = "max-w-2xl", onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="intel-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative w-full ${width} bg-[#F8F7F3] shadow-2xl overflow-y-auto`}>
        <div className="sticky top-0 z-10 bg-[#F8F7F3]/95 backdrop-blur border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-medium text-[#0B2545]">{title}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#E4E1D8]" data-testid="intel-drawer-close">
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>
        <div className="px-6 py-6">{children}</div>
      </div>
    </div>
  );
}
